
import { forwardRef } from 'react';
import { ResultsHeader } from "./ResultsHeader";
import { Industry, OrganizationSize, OrganizationType, OrganizationDetails } from "@/types/assessment";
import { BenchmarkSection } from "./sections/BenchmarkSection";
import { StrategicRoadmap } from "./sections/StrategicRoadmap";
import { DimensionAnalysis } from "./sections/DimensionAnalysis";
import { ExecutiveSummary } from "./sections/ExecutiveSummary";

interface PDFContentProps {
  organizationDetails: OrganizationDetails;
  maturityLevel: string;
  overallScore: number;
  description: string;
  dimensionScores: Record<string, number>;
  className?: string;
  assessmentId: string;
}

export const PDFContent = forwardRef<HTMLDivElement, PDFContentProps>(({ 
  organizationDetails,
  maturityLevel,
  overallScore,
  description,
  dimensionScores,
  assessmentId
}, ref) => {
  const lowestScore = Math.min(...Object.values(dimensionScores));
  const lowestDimension = Object.entries(dimensionScores).find(([_, score]) => score === lowestScore)?.[0] || '';

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const pdfSectionStyle = "mb-20 pb-8 print:page-break-after-always";

  return (
    <div 
      ref={ref}
      data-organization-name={organizationDetails.name}
      className="hidden" // Hide from normal view
      style={{
        position: 'absolute',
        left: '-9999px',
        top: 0,
        width: '297mm', // A4 landscape width
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}
    >
      {/* Cover Page */}
      <div data-pdf-section="Cover Page" className={pdfSectionStyle}>
        <div className="min-h-[210mm] flex flex-col justify-between">
          <div className="flex-1 flex flex-col items-center justify-center gap-12 py-20">
            <img 
              src="/lovable-uploads/fd10e402-c74e-4298-af26-7ca7744214ae.png" 
              alt="AIMAA Logo" 
              className="h-32 w-auto mb-8"
              crossOrigin="anonymous"
            />
            <div className="text-center space-y-6">
              <h1 className="text-5xl font-bold text-[#0f2d69]">
                AI Maturity Assessment Report
              </h1>
              <p className="text-3xl text-gray-600">Comprehensive Analysis & Recommendations</p>
              <div className="mt-16 space-y-3">
                <p className="text-2xl text-gray-700">{organizationDetails.name}</p>
                <p className="text-xl text-gray-500">
                  Assessment Date: {formatDate(organizationDetails.assessmentDate)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Organization Profile */}
      <div data-pdf-section="Organization Profile" className={pdfSectionStyle}>
        <h2 className="text-3xl font-bold text-[#0f2d69] mb-8">Organization Profile</h2>
        <ResultsHeader 
          organizationName={organizationDetails.name}
          assessmentDate={organizationDetails.assessmentDate}
          industry={organizationDetails.industry}
          organizationSize={organizationDetails.size}
          organizationType={organizationDetails.organizationType}
          country={organizationDetails.country}
          assessorName={organizationDetails.assessorName}
          assessorRole={organizationDetails.assessorRole}
          assessorEmail={organizationDetails.assessorEmail}
        />
      </div>

      {/* Executive Summary */}
      <div data-pdf-section="Executive Summary" className={pdfSectionStyle}>
        <h2 className="text-3xl font-bold text-[#0f2d69] mb-8">Executive Summary</h2>
        <ExecutiveSummary 
          organizationName={organizationDetails.name}
          overallScore={overallScore}
          maturityLevel={maturityLevel}
          description={description}
          industry={organizationDetails.industry}
          lowestDimension={lowestDimension}
          lowestScore={lowestScore}
        />
      </div>

      {/* Dimension Analysis */}
      <div data-pdf-section="Dimension Analysis" className={pdfSectionStyle}>
        <h2 className="text-3xl font-bold text-[#0f2d69] mb-8">Dimension Analysis</h2>
        <DimensionAnalysis 
          dimensionScores={dimensionScores}
          industry={organizationDetails.industry}
        />
      </div>

      {/* Industry Benchmark */}
      <div data-pdf-section="Industry Benchmark" className={pdfSectionStyle}>
        <h2 className="text-3xl font-bold text-[#0f2d69] mb-8">Industry Benchmark Analysis</h2>
        <BenchmarkSection 
          dimensionScores={dimensionScores}
          industry={organizationDetails.industry}
          organizationDetails={organizationDetails}
        />
      </div>

      {/* Strategic Roadmap */}
      <div data-pdf-section="Strategic Roadmap" className={pdfSectionStyle}>
        <h2 className="text-3xl font-bold text-[#0f2d69] mb-8">Strategic Roadmap</h2>
        <StrategicRoadmap 
          maturityLevel={maturityLevel}
          dimensionScores={dimensionScores}
          lowestDimension={lowestDimension}
        />
      </div>

      {/* Key Recommendations */}
      <div data-pdf-section="Key Recommendations" className={pdfSectionStyle}>
        <h2 className="text-3xl font-bold text-[#0f2d69] mb-8">Key Recommendations</h2>
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4">Primary Focus Areas</h3>
            <ul className="list-disc list-inside space-y-3">
              <li>Strengthen {lowestDimension} capabilities to improve from current score of {lowestScore.toFixed(1)}</li>
              <li>Implement structured approach to advance from {maturityLevel} maturity level</li>
              <li>Develop comprehensive training programs aligned with industry standards</li>
              <li>Establish clear metrics and KPIs for measuring AI implementation success</li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-xl font-semibold mb-4">Next Steps</h3>
            <ul className="list-disc list-inside space-y-3">
              <li>Review and prioritize recommendations based on business impact</li>
              <li>Develop detailed implementation plan with clear milestones</li>
              <li>Allocate necessary resources and establish timeline</li>
              <li>Set up regular review cycles to track progress</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
});

PDFContent.displayName = 'PDFContent';
